"use client"

import { Link } from 'react-router-dom'
import { ArrowLeft, Video } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { TextToVideoGallery } from '@/components/gallery/TextToVideoGallery'
import { TextImageToVideoGallery } from '@/components/gallery/TextImageToVideoGallery'
import { VideoGalleryItem } from '@/models/gallery'
// Importing the StyleX styles
import { videoPageStyles } from '@/styles/VideoPage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'Pika Video',
    shortName: 'Pika',
    modelKey: 'pika-video',
    creditsPerVideo: 20,
    heroBgVideo: 'https://cdn.aibrush.co/docs/videos/models/pika-video/hero-bg.mp4',
    heroBgPoster: 'https://cdn.aibrush.co/docs/videos/models/pika-video/hero-bg.png',
    providerUrl: 'https://pika.art/',
    providerName: 'Pika Labs',
    pageUrl: 'https://docs.aibrush.co/videos-models/pika-video',
} as const;

const textToVideoGalleryItems: VideoGalleryItem[] = [
    {
        id: "pika-video-1",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-1.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-1.png",
        className: "",
        prompt: "A cinematic, 3D animated scene. A world-weary, anthropomorphic red fox detective, wearing a classic, rain-soaked trench coat and fedora, walks deliberately down a narrow, grimy city alleyway. The brick walls are covered in graffiti. The ground is slick with rain, reflecting the flickering red neon sign of a 'Jazz Club' and a single, faulty overhead streetlamp. The camera starts on a close-up of a puddle rippling and then tracks backwards at a low angle, revealing the fox as he approaches. -style pixar, film noir, dramatic shadows, volumetric lighting -motion moderate"
    },
    {
        id: "pika-video-2",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-2.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-2.png",
        className: "",
        prompt: "lone hacker on a hoverbike speeding through rain-slicked streets of a futuristic Tokyo, neon signs reflecting in puddles, pursued by holographic drones, in a gritty, high-contrast cyberpunk aesthetic."
    },
    {
        id: "pika-video-7",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-7.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-7.png",
        className: "wide",
        prompt: `A young girl in a bright yellow summer dress runs in ultra slow motion across a field of lush green grass, her face alight with pure joy. She is chasing a swarm of large, iridescent soap bubbles that drift dreamily through the air. The scene is backlit by the warm, hazy glow of golden hour sunlight, creating a beautiful lens flare and a soft bokeh effect on the background where her parents are watching. The camera focuses on a single bubble, following it as it floats towards the girl's outstretched hands. -camera slow motion, dreamy, shallow depth of field -motion gentle`
    },
    {
        id: "pika-video-3",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-3.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-3.png",
        className: "",
        prompt: `A mythical and visually intricate video animated in the style of ancient mosaic tiles. The video shows the rebirth of a phoenix from volcanic ashes in a ruined Greek temple...`
    },
    {
        id: "pika-video-4",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-4.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-4.png",
        className: "tall",
        prompt: "Hyper-realistic video. A man in high-end athletic gear runs with powerful, rhythmic strides down the center of a completely deserted avenue, flanked by towering glass skyscrapers. It is dawn, and the low, golden sun creates an extreme long shadow that stretches out behind him. His breath fogs in the crisp morning air. The camera is positioned low to the ground, tracking smoothly alongside him, emphasizing his speed and the vast, empty urban landscape. -style photorealistic, cinematic, clean -motion high"
    },
    {
        id: "pika-video-5",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-5.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-5.png",
        className: "",
        prompt: `A vibrant and whimsical underwater fantasy video in a style reminiscent of classic Disney animation...`
    },
    {
        id: "pika-video-6",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-6.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-6.png",
        className: "tall",
        prompt: `A young woman with disheveled hair, wearing a simple nightgown, sits on the edge of her unmade bed. The room is dark, only illuminated by cool moonlight filtering through window blinds...`
    },
    {
        id: "pika-video-8",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-8.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-8.png",
        className: "",
        prompt: `Two roommates are having a heated argument in their cramped, messy apartment...`
    },
    {
        id: "pika-video-9",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-9.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-9.png",
        className: "",
        prompt: `A man with wide, terrified eyes is crouched behind a large, graffiti-covered metal dumpster in a dark, wet alley...`
    },
    {
        id: "pika-video-10",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-10.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-10.png",
        className: "",
        prompt: `Raw documentary-style footage from within a massive crowd of protestors marching in a downpour...`
    },
    {
        id: "pika-video-11",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-11.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-11.png",
        className: "",
        prompt: `A tense, first-person point-of-view (POV) shot. The camera shows the perspective of someone exploring a derelict, gothic-style mansion...`
    },
    {
        id: "pika-video-12",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-12.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-12.png",
        className: "",
        prompt: `A vibrant, bustling night market in Southeast Asia...`
    },
    {
        id: "pika-video-13",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-13.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-13.png",
        className: "",
        prompt: `A surreal, hyper-detailed video of a single, giant bioluminescent jellyfish pulsing with soft, internal light...`
    },
];

const textImageToVideoGalleryItems: VideoGalleryItem[] = [
    {
        id: "pika-video-14",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-14.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-14.png",
        className: "",
        prompt: "The knight's crimson cape billows and whips violently in the wind. The sea of clouds below drifts slowly to the right. The camera performs a slow, majestic dolly zoom, pushing in on the knight's solitary figure. -motion high -camera dolly in",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-14.png"]
    },
    {
        id: "pika-video-15",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-15.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-15.png",
        className: "",
        prompt: "Gentle, concentric ripples emanate outwards from the single red maple leaf, disturbing the still water. The camera performs a slow, smooth pan upwards, tilting to reveal the details of the stone lantern in the background. -motion gentle -camera pan up",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-15.png"]
    },
    {
        id: "pika-video-16",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-16.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-16.png",
        className: "",
        prompt: `The ethereal god rays of sunlight shimmer and dance, their direction shifting slowly as if the water is flowing. Tiny, bright bubbles float gently and consistently upwards from the bottom of the frame. The camera rotates slowly clockwise, creating a graceful, weightless feeling. -motion gentle -camera rotate clockwise`,
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-16.png"]
    },
    {
        id: "pika-video-17",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-17.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-17.png",
        className: "",
        prompt: `The pink and blue neon signs flicker erratically. The light rain intensifies into a downpour, with visible droplets streaking down and splashing into the puddles on the ground, creating ripples that distort the neon reflections. -motion moderate`,
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-17.png"]
    },
    {
        id: "pika-video-18",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-18.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-18.png",
        className: "",
        prompt: `The thick plume of cigarette smoke rises and curls elegantly, dancing in the single, static beam of light. The camera performs a very slow dolly zoom outwards, revealing more of the dark office and the city skyline silhouette in the window. -motion gentle -camera dolly out`,
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-18.png"]
    },
    {
        id: "pika-video-19",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-19.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-19.png",
        className: "",
        prompt: `The purple and orange gases of the nebula swirl slowly and majestically in the background. The four engines on the spaceship begin to pulse with a growing, intense blue light, causing a bright lens flare. The camera shakes slightly, as if from the vibrations of the ship powering up. -motion moderate -camera shake`,
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-19.png"]
    },
    {
        id: "pika-video-20",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-20.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-20.png",
        className: "",
        prompt: `The dragon's eyelid performs a single, slow, intimidating blink, the heavy, scaled lid sweeping down and then up. The wisp of smoke continues to curl and drift slowly upwards across the frame. The camera executes an almost imperceptible, extremely slow zoom in, creating intense tension. -motion gentle -camera zoom in`,
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-20.png"]
    },
    {
        id: "pika-video-21",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-21.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-21.png",
        className: "",
        prompt: `The delicate white steam rises continuously from the coffee mug, swirling and dissipating into the air. In the background, the raindrops on the window slowly streak downwards, merging with other drops. -motion gentle`,
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-21.png"]
    },
    {
        id: "pika-video-22",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-22.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-22.png",
        className: "",
        prompt: `The candle flame dances and flickers dynamically, as if disturbed by a gentle draft. This causes the large, soft shadow on the wall behind it to move, warp, and dance in perfect sync with the flame's movement. -motion moderate`,
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-22.png"]
    },
    {
        id: "pika-video-23",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-23.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-23.png",
        className: "",
        prompt: `The embers pulse with a slow, deep, breathing rhythm, their orange and red glow intensifying and then slightly dimming. Small, bright yellow sparks occasionally erupt from the embers and float upwards out of the frame. -motion gentle`,
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-23.png"]
    },
    {
        id: "pika-video-24",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-24.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-24.png",
        className: "",
        prompt: `The massive plume of white smoke billows and expands powerfully upwards and outwards, slowly dissipating into the air. The entire camera frame shakes with a slight, powerful vibration, simulating the idling energy of the massive steam engine. -motion high -camera shake`,
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-24.png"]
    },
    {
        id: "pika-video-25",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/sample-25.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-25.png",
        className: "",
        prompt: ` The long, golden pendulum at the bottom begins to swing back and forth in a steady, rhythmic motion. The thin second hand on the clock face ticks forward in distinct, one-second steps. The camera performs a slow dolly zoom out, revealing the full, majestic height of the grandfather clock. -motion moderate -camera dolly out`,
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-25.png"]
    }
];

const allTextImageToVideoImages: string[] = [
    "https://cdn.aibrush.co/docs/videos/models/pika-video/thumbnail-14.png"
];

export default function PikaVideoPage() {
    return (
        <div {...stylex.props(videoPageStyles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(videoPageStyles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(videoPageStyles.heroSection)}>
                <div {...stylex.props(videoPageStyles.heroBackground)}>
                    <video
                        poster={MODEL_CONFIG.heroBgPoster}
                        src={MODEL_CONFIG.heroBgVideo}
                        {...stylex.props(videoPageStyles.heroBgVideo)}
                        autoPlay
                        muted
                        playsInline
                    />
                    <div {...stylex.props(videoPageStyles.heroOverlay)}></div>
                </div>
                <div {...stylex.props(videoPageStyles.heroContent)}>
                    <h2 {...stylex.props(videoPageStyles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(videoPageStyles.heroDescription)}>
                        Experience cutting-edge AI video generation with <strong>Text to Video</strong> and <strong>Text & Image to Video</strong> capabilities.
                        Create stunning, high-quality videos with unmatched creativity and precision.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(videoPageStyles.heroStats)}>
                        <div {...stylex.props(videoPageStyles.stat)}>
                            <Video size={20} {...stylex.props(videoPageStyles.statSvg)} />
                            <span>Text to Video</span>
                        </div>
                        <div {...stylex.props(videoPageStyles.stat)}>
                            <Video size={20} {...stylex.props(videoPageStyles.statSvg)} />
                            <span>Text & Image to Video</span>
                        </div>
                    </div>
                    <div {...stylex.props(videoPageStyles.creditCostBadge)}>{MODEL_CONFIG.creditsPerVideo} credits per video</div>
                </div>
            </section>

            {/* Video Gallery */}
            <TextToVideoGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.name}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerVideo} credits</strong> per video.`}
                title="Text to Video Gallery"
                description={`Explore the stunning capabilities of <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a> through these AI-generated videos. <strong>Click on any video</strong> to see how it was created.`}
                items={textToVideoGalleryItems} />

            {/* Text & Image to Video Gallery */}
            <TextImageToVideoGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.name}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerVideo} credits</strong> per video.`}
                title="Text & Image to Video Gallery"
                description={`Experience advanced video generation with image references using <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a>. Transform static images into dynamic videos with precise control. <strong>Click on any video</strong> to see how it was created.`}
                items={textImageToVideoGalleryItems}
                allImages={allTextImageToVideoImages}
            />
        </div>
    )
}